/**
 * Страница аккаунта: шапка с кредами (админам), стат-карточки, график
 * динамики просмотров, список всех рилсов (от парсера или руками), история.
 */
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { AccountDetail, ChartRange, Reel, User } from '@wml-insta/shared';
import { CHART_RANGES } from '@wml-insta/shared';
import { api } from '../api/client.js';
import { loadPersisted, savePersisted } from '../lib/persist.js';
import { FIELD_LABELS, formatDate, formatDateTime, formatNum, reelsUrl } from '../lib/format.js';
import { SnapshotChart } from '../charts/SnapshotChart.js';
import { UpdatedNote } from '../ui/UpdatedNote.js';
import { ConfirmDialog } from '../ui/ConfirmDialog.js';
import { ModalPortal } from '../ui/ModalPortal.js';

function CopyRow({ label, value }: { label: string; value: string | null }) {
  const [copied, setCopied] = useState(false);
  return (
    <>
      <span className="k">{label}</span>
      {value ? (
        <b style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          <code style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}</code>
          <button
            className="copy-mini"
            title={copied ? 'Скопировано' : 'Скопировать'}
            onClick={() => {
              navigator.clipboard.writeText(value).then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 1200);
              }).catch(() => {});
            }}
          >
            {copied ? (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12" /></svg>
            ) : (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15V5a2 2 0 0 1 2-2h10" /></svg>
            )}
          </button>
        </b>
      ) : (
        <b style={{ color: 'var(--text-subtle)' }}>—</b>
      )}
    </>
  );
}

function validRange(v: string, fallback: ChartRange = '7d'): ChartRange {
  return (CHART_RANGES as string[]).includes(v) ? (v as ChartRange) : fallback;
}

const RANGES: { key: ChartRange; label: string }[] = [
  { key: '7d', label: '7 дней' },
  { key: '1m', label: 'Месяц' },
  { key: '3m', label: '3 месяца' },
  { key: 'all', label: 'Всё время' },
];

function rangeFrom(range: ChartRange): string | null {
  if (range === 'all') return null;
  const days = range === '7d' ? 7 : range === '1m' ? 30 : 90;
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

export function AccountPage({ me }: { me: User }) {
  const { id } = useParams();
  const nav = useNavigate();
  const [detail, setDetail] = useState<AccountDetail | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [addingReel, setAddingReel] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Последний таймфрейм графика — запоминается на юзера.
  const [range, setRangeRaw] = useState<ChartRange>(() => validRange(loadPersisted<string>(me.id, 'chart.account.range', 'all'), 'all'));
  const setRange = (r: ChartRange) => { setRangeRaw(r); savePersisted(me.id, 'chart.account.range', r); };
  const isAdmin = me.role === 'admin';

  function load() {
    if (!id) return;
    api<AccountDetail>(`/api/accounts/${id}`).then(setDetail).catch(() => setNotFound(true));
  }
  useEffect(load, [id]);

  const chartPoints = useMemo(() => {
    if (!detail) return [];
    const from = rangeFrom(range);
    return from ? detail.snapshots.filter((sn) => sn.date >= from) : detail.snapshots;
  }, [detail, range]);

  if (notFound) return <div className="empty-state">Аккаунт не найден.</div>;
  if (!detail) return <div className="empty-state">Загрузка…</div>;

  const a = detail.account;
  const reelViews = detail.reels.reduce((s, r) => s + r.views, 0);

  async function removeReel(reel: Reel) {
    await api(`/api/accounts/reels/${reel.id}`, { method: 'DELETE' }).catch(() => {});
    load();
  }

  return (
    <div className="page-inner" style={{ maxWidth: 1250 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap', marginBottom: 18 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <h1 style={{ margin: 0, fontSize: 24, letterSpacing: '-0.02em' }}>@{a.username}</h1>
            {a.status
              ? <span className={`status-chip status-${a.status.replace(' ', '_')}`}>{a.status}</span>
              : <span className="status-chip status-none">без статуса</span>}
            {a.warn && <span className="status-chip status-Прогрев">варн</span>}
          </div>
          <div style={{ color: 'var(--text-subtle)', fontSize: 13, marginTop: 5 }}>
            <Link to={`/phone/${a.phoneId}`} style={{ color: 'var(--text-muted)' }}>{a.phoneName}</Link>
            {a.containerId != null && <> · контейнер {a.containerId}</>}
            {a.modelId && <> · <Link to={`/model/${a.modelId}`} style={{ color: 'var(--text-muted)' }}>{a.modelName}</Link></>}
            {a.startDate && <> · старт {formatDate(a.startDate)}</>}
            {a.banDate && <> · бан {formatDate(a.banDate)}</>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <a className="link-out" href={reelsUrl(a.igUrl, a.username)!} target="_blank" rel="noreferrer">Instagram ↗</a>
          {a.link && <a className="link-out" href={`https://${a.link.replace(/^https?:\/\//, '')}`} target="_blank" rel="noreferrer">Линк ↗</a>}
        </div>
      </div>

      <div className="stats-row" style={{ gridTemplateColumns: 'repeat(4, 1fr)', margin: '0 0 18px' }}>
        <div className="stat-card">
          <div className="stat-label">Просмотры 24ч</div>
          <div className="stat-value">{formatNum(a.views24h)}</div>
          <UpdatedNote at={a.lastParsedAt} variant="block" />
        </div>
        <div className="stat-card">
          <div className="stat-label">Видео 24ч</div>
          <div className="stat-value">{formatNum(a.videos24h)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Просмотры всего</div>
          <div className="stat-value stat-accent">{formatNum(a.viewsTotal)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Категория</div>
          <div className="stat-value" style={{ fontSize: 15, lineHeight: 1.3, whiteSpace: 'normal' }}>{a.viewsCategory ?? '—'}</div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isAdmin ? '1fr 1fr' : '1fr', gap: 14, marginBottom: 18 }}>
        <div className="kv">
          <span className="k">Тип аккаунта</span><b>{a.accountType ?? '—'}</b>
          <span className="k">Пачка</span><b>{a.batch ?? '—'}</b>
          <span className="k">Крео</span><b>{a.creoName ?? '—'}</b>
          <span className="k">Версия клона</span><b>{a.cloneVersion ?? '—'}</b>
          <span className="k">Оформление</span><b>{a.decorated ? 'да' : 'нет'}</b>
          <span className="k">Залито</span><b>{a.poured ? 'да' : 'нет'}</b>
          <span className="k">Замена крео</span><b>{a.creoReplaced ? `да${a.creoReplaceReason ? ` · ${a.creoReplaceReason}` : ''}` : 'нет'}</b>
          <span className="k">Последний парсинг</span><b>{a.lastParsedAt ? formatDateTime(a.lastParsedAt) : '—'}</b>
          {a.note && (<><span className="k">Заметка</span><b>{a.note}</b></>)}
        </div>
        {isAdmin && (
          <div className="kv">
            <CopyRow label="Юзернейм" value={a.username} />
            <CopyRow label="Пароль" value={a.password} />
            <CopyRow label="2ФА ключ" value={a.totpSecret} />
            <CopyRow label="Почта" value={a.email} />
            <CopyRow label="Пароль почты" value={a.emailPassword} />
          </div>
        )}
      </div>

      <div className="card card-large" style={{ marginBottom: 18 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
          <div className="section-title" style={{ margin: 0 }}>Динамика просмотров</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <UpdatedNote at={a.lastParsedAt} />
            <div className="range-tabs range-tabs-compact">
              {RANGES.map((r) => (
                <button key={r.key} className={range === r.key ? 'active' : ''} onClick={() => setRange(r.key)}>
                  {r.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        <SnapshotChart points={chartPoints} height={280} />
      </div>

      <div className="card" style={{ marginBottom: 18 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 10 }}>
          <div className="section-title" style={{ margin: 0 }}>
            Рилсы ({detail.reels.length}
            {detail.reels.some((r) => r.deletedAt) && (
              <span style={{ color: 'var(--accent)' }}> · {detail.reels.filter((r) => r.deletedAt).length} удалено</span>
            )}
            {reelViews > 0 ? ` · ${formatNum(reelViews)} просм. (вкл. удалённые)` : ''})
          </div>
          <button className="btn-secondary" onClick={() => setAddingReel(true)}>+ Добавить рилс</button>
        </div>
        {detail.reels.length === 0 ? (
          <div className="empty-state" style={{ padding: '28px 16px' }}>
            Рилсов пока нет — появятся автоматически, когда подключим парсер, или добавь вручную.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="reels-table">
              <thead>
                <tr><th>#</th><th>Дата</th><th>Ссылка</th><th style={{ textAlign: 'right' }}>Просмотры</th><th style={{ textAlign: 'right' }}>Лайки</th><th style={{ textAlign: 'right' }}>Комменты</th><th /></tr>
              </thead>
              <tbody>
                {detail.reels.map((r, i) => (
                  <tr key={r.id} className={r.deletedAt ? 'reel-row-deleted' : ''}>
                    <td style={{ color: 'var(--text-subtle)' }}>{i + 1}</td>
                    <td>{formatDate(r.publishedAt)}</td>
                    <td style={{ maxWidth: 380, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <a href={r.url} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)' }}>{r.url.replace(/^https?:\/\/(www\.)?/, '')}</a>
                      {r.deletedAt && (
                        <span className="reel-deleted-badge" title={`Инста удалила рилс · ${new Date(r.deletedAt).toLocaleString('ru-RU')}. Метрики заморожены на последнем отчёте и учитываются в сводках.`}>
                          удалён {formatDate(r.deletedAt.slice(0, 10))}
                        </span>
                      )}
                      {r.caption && <span style={{ color: 'var(--text-subtle)', marginLeft: 8 }}>{r.caption}</span>}
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatNum(r.views)}</td>
                    <td style={{ textAlign: 'right' }}>{formatNum(r.likes)}</td>
                    <td style={{ textAlign: 'right' }}>{formatNum(r.comments)}</td>
                    <td style={{ textAlign: 'right' }}>
                      <button className="link-btn danger" onClick={() => removeReel(r)}>удалить</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {detail.events.length > 0 && (
        <div className="card" style={{ marginBottom: 18 }}>
          <div className="section-title">История</div>
          <div className="event-feed">
            {detail.events.map((e) => (
              <div key={e.id} className="event-row">
                <span className="ev-time">{formatDateTime(e.createdAt)}</span>
                <span className="ev-text">
                  {e.userName && <span className="ev-user">{e.userName} </span>}
                  {e.field === 'reel_deleted'
                    ? <>рилс <span style={{ color: 'var(--accent)' }}>удалён инстой</span>: {e.newValue} ({e.oldValue})</>
                    : e.field === 'created'
                    ? <>создал аккаунт{e.newValue?.startsWith('импорт') ? ` (${e.newValue})` : ''}</>
                    : <>{FIELD_LABELS[e.field] ?? e.field}: <b>{e.oldValue ?? '—'}</b> → <b>{e.newValue ?? '—'}</b></>}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {isAdmin && (
        <button className="link-btn danger" onClick={() => setConfirmDelete(true)}>
          Удалить аккаунт
        </button>
      )}

      {addingReel && (
        <AddReelModal
          accountId={a.id}
          onClose={() => setAddingReel(false)}
          onAdded={() => { setAddingReel(false); load(); }}
        />
      )}

      {confirmDelete && (
        <ConfirmDialog
          title="Удалить аккаунт?"
          message={<>Аккаунт <b>@{a.username}</b> будет удалён вместе с историей, снапшотами и рилсами. Это действие необратимо.</>}
          confirmLabel="Удалить"
          danger
          busy={deleting}
          onConfirm={async () => {
            setDeleting(true);
            try {
              await api(`/api/accounts/${a.id}`, { method: 'DELETE' });
              nav(-1);
            } catch {
              setDeleting(false);
              setConfirmDelete(false);
            }
          }}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
    </div>
  );
}

function AddReelModal({ accountId, onClose, onAdded }: { accountId: string; onClose: () => void; onAdded: () => void }) {
  const [form, setForm] = useState({ url: '', publishedAt: '', views: '', likes: '', comments: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await api(`/api/accounts/${accountId}/reels`, {
        method: 'POST',
        body: JSON.stringify({
          url: form.url.trim(),
          publishedAt: form.publishedAt || null,
          views: form.views === '' ? 0 : Number(form.views),
          likes: form.likes === '' ? 0 : Number(form.likes),
          comments: form.comments === '' ? 0 : Number(form.comments),
        }),
      });
      onAdded();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Не получилось');
      setBusy(false);
    }
  }

  return (
    <ModalPortal>
    <div className="modal-backdrop" onClick={busy ? undefined : onClose}>
      <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
        <div className="modal-title">Добавить рилс</div>
        <form onSubmit={submit} style={{ display: 'grid', gap: 10 }}>
          <label className="field">
            <span>Ссылка на рилс *</span>
            <input className="admin-input" required autoFocus value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://www.instagram.com/reel/…" />
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <label className="field">
              <span>Дата публикации</span>
              <input className="admin-input" type="date" value={form.publishedAt} onChange={(e) => setForm({ ...form, publishedAt: e.target.value })} />
            </label>
            <label className="field">
              <span>Просмотры</span>
              <input className="admin-input" type="number" min={0} value={form.views} onChange={(e) => setForm({ ...form, views: e.target.value })} />
            </label>
            <label className="field">
              <span>Лайки</span>
              <input className="admin-input" type="number" min={0} value={form.likes} onChange={(e) => setForm({ ...form, likes: e.target.value })} />
            </label>
            <label className="field">
              <span>Комменты</span>
              <input className="admin-input" type="number" min={0} value={form.comments} onChange={(e) => setForm({ ...form, comments: e.target.value })} />
            </label>
          </div>
          {err && <div className="error">{err}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
            <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>Отмена</button>
            <button type="submit" className="btn-primary" disabled={busy} style={{ width: 'auto', padding: '0 20px' }}>
              {busy ? 'Добавляю…' : 'Добавить'}
            </button>
          </div>
        </form>
      </div>
    </div>
    </ModalPortal>
  );
}
